import React from 'react'

export type DraggableItem = {
  id: string
  label: string
  right?: React.ReactNode
  disabled?: boolean
}

export function DraggableList({ items, onReorder, renderRight }: {
  items: DraggableItem[]
  onReorder: (ids: string[]) => void
  renderRight?: (item: DraggableItem) => React.ReactNode
}) {
  const [order, setOrder] = React.useState(items.map(i => i.id))
  React.useEffect(() => setOrder(items.map(i => i.id)), [items.map(i => i.id).join('|')])

  const dragId = React.useRef<string | null>(null)
  const [overId, setOverId] = React.useState<string | null>(null)
  const [dragging, setDragging] = React.useState(false)

  function handleDragStart(id: string) {
    dragId.current = id
    setDragging(true)
  }
  function handleDragOver(e: React.DragEvent) { e.preventDefault() }
  function handleDragEnter(targetId: string) {
    setOverId(targetId)
    const src = dragId.current
    if (!src || src === targetId) return
    // Trello-like: reordena durante o arraste para mostrar a animação
    const next = order.filter(id => id !== src)
    const idx = next.indexOf(targetId)
    if (idx >= 0) {
      next.splice(idx, 0, src)
      setOrder(next)
    }
  }
  function handleDrop(_targetId: string) {
    onReorder(order)
    setOverId(null)
    setDragging(false)
    dragId.current = null
  }

  const byId = new Map(items.map(i => [i.id, i]))

  return (
    <div className="space-y-2">
      {order.map(id => {
        const it = byId.get(id)
        if (!it) return null
        return (
          <div
            key={id}
            className={[
              'flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2 select-none',
              'transition-all duration-200',
              dragging && dragId.current === id ? 'opacity-70 ring-2 ring-primary border-primary/50' : 'border-border',
              overId === id && dragId.current !== id ? 'outline outline-2 outline-dashed outline-primary/50' : '',
            ].join(' ')}
            draggable={!it.disabled}
            onDragStart={() => handleDragStart(id)}
            onDragOver={handleDragOver}
            onDragEnter={() => handleDragEnter(id)}
            onDrop={() => handleDrop(id)}
          >
            <div className="text-sm">{it.label}</div>
            <div className="ml-3">{renderRight ? renderRight(it) : it.right}</div>
          </div>
        )
      })}
    </div>
  )
}
